import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './SearchOptions.css';
import { Helmet } from 'react-helmet-async';

/**
 * SearchOptions Component
 *
 * This component allows users to search for LGBTQ+ protections based on their preferences.
 * It provides three search options: Basic Search, Advanced Search, and City Comparison.
 *
 * The Basic Search offers a simplified set of protection categories.
 * The Advanced Search provides detailed options organized into categories on one page.
 * The City Comparison redirects users to the dedicated CompareSearch page.
 *
 * Each protection requires the user to select one checkbox: "doesn't matter", "good to have", or "must have".
 */

// Law classifications - which laws apply to SO, GI, or both
const LAW_CLASSIFICATIONS = {
  1: ['sexual'], 2: ['sexual'], 3: ['sexual'], 4: ['both'], 5: ['both'],
  6: ['sexual'], 7: ['both'], 8: ['both'], 9: ['both'], 10: ['both'],
  11: ['both'], 12: ['both'], 13: ['both'], 14: ['both'], 15: ['gender'],
  16: ['gender'], 17: ['both'], 18: ['both'], 19: ['both'], 20: ['sexual'],
  21: ['sexual'], 22: ['both'], 23: ['both'], 24: ['both'], 25: ['both'],
  26: ['both'], 27: ['both'], 28: ['sexual'], 29: ['gender'], 30: ['gender'],
  31: ['gender'], 32: ['both'], 33: ['both'], 34: ['both'], 35: ['gender'],
  36: ['gender'], 37: ['gender'], 38: ['gender'], 39: ['gender'], 40: ['both'],
  41: ['both'], 42: ['both'], 43: ['both'], 44: ['both'], 45: ['both'],
  46: ['both'], 47: ['both'], 48: ['both'], 49: ['both'], 50: ['both'],
  51: ['gender'], 52: ['gender'], 53: ['gender'], 54: ['gender'], 55: ['gender']
};

// Mapping from search options to law IDs
const searchOptionToLaws = {
  // Basic search options
  employmentProtection: [9, 10, 11],
  stateEmployees: [13],
  relationship: [1, 4, 5, 6, 7, 8],
  healthcare: [34, 36, 37, 38, 39, 42, 43, 35, 19],
  genderMarkers: [51, 52],
  youthProtections: [22, 23, 24, 32, 29, 33, 25, 27, 31, 26, 18],
  criminalJustice: [46, 47, 50],
  noBans: [14],
  noBathroom: [15],
  noExclusionarySexDefinition: [16],
  noReligiousExemptions: [21, 19, 18],

  // Advanced search options
  employmentNonDiscrimination: [9],
  housingNonDiscrimination: [10],
  publicAccommodationNonDiscrimination: [11],
  stateEmployeeNonDiscrimination: [13],
  allowLocalLaws: [14],
  noReligiousExemptionMarriage: [21],
  secondParentAdoption: [1],
  adoptionNonDiscrimination: [4],
  fosterCareNonDiscrimination: [5],
  assistedReproductionRecognition: [6],
  lgbtInclusiveSpouseForLeave: [7],
  lgbtInclusiveChildrenForLeave: [8],
  privateHealthInsuranceNonDiscrimination: [34],
  transgenderYouthCare: [35],
  shieldLawsTransgenderCare: [36],
  healthInsuranceTransgenderCoverage: [37],
  medicaidTransgenderCoverage: [38],
  stateEmployeeTransgenderBenefits: [39],
  fertilityPrivateInsurance: [42],
  fertilityMedicaid: [43],
  noReligiousExemptionMedical: [19],
  driverLicenseGenderMarker: [51],
  birthCertificateGenderMarker: [52],
  bathroomAccess: [15],
  noExclusionarySexDefinition: [16],
  studentNonDiscrimination: [22],
  antibullyingLaws: [23],
  allowLocalStudentProtections: [24],
  allowLocalAntibullyingPolicies: [24],
  lgbtInclusiveCurriculum: [25],
  noDontSayGay: [26],
  noParentalOptOut: [27],
  transgenderSportsParticipation: [29],
  noForcedOuting: [31],
  conversionTherapyBan: [32],
  childWelfareSystemProtections: [33],
  noReligiousExemptionChildWelfare: [18],
  hateCrimeLaws: [46],
  noPanicDefense: [47],
  noDragRestrictions: [50]
};

// Tooltip component
const Tooltip = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
      let left = triggerRect.right + 8;

      // Adjust if tooltip goes off screen
      if (left + tooltipRect.width > viewportWidth) {
        left = triggerRect.left - tooltipRect.width - 8;
      }

      top = Math.max(5, Math.min(top, viewportHeight - tooltipRect.height - 5));
      left = Math.max(5, Math.min(left, viewportWidth - tooltipRect.width - 5));

      setPosition({ top, left });
    }
  }, [isVisible]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        style={{ position: 'relative', cursor: 'help' }}
      >
        {children}
      </span>
      {isVisible && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 9999,
            pointerEvents: 'none'
          }}
          className="tooltip-container"
        >
          <div
            className="tooltip-content"
            style={{
              background: '#1F2937',
              color: 'white',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              lineHeight: '1.4',
              maxWidth: '250px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              fontWeight: 'normal'
            }}
          >
            {content}
          </div>
          <div
            className="tooltip-arrow"
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              left: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '6px solid #1F2937'
            }}
          />
        </div>
      )}
    </>
  );
};

// Helper function to check if a search option should be shown based on protection types
const shouldShowSearchOption = (searchOption, protections) => {
  const lawIds = searchOptionToLaws[searchOption] || [];

  // If no laws mapped, show the option (safe default)
  if (lawIds.length === 0) return true;

  // If both protections are selected, show all options
  if (protections.sexualOrientation && protections.genderIdentity) {
    return true;
  }

  // For basic search, we should show the option unless ALL laws are exclusively for the other type
  if (protections.sexualOrientation && !protections.genderIdentity) {
    // User selected SO only - hide options where ALL laws are gender-only
    return lawIds.some(lawId => {
      const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
      return classifications.includes('sexual') || classifications.includes('both');
    });
  }

  if (!protections.sexualOrientation && protections.genderIdentity) {
    // User selected GI only - hide options where ALL laws are sexual-only
    return lawIds.some(lawId => {
      const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
      return classifications.includes('gender') || classifications.includes('both');
    });
  }

  return true; // Default to showing if no protections selected
};

/**
 * Filters law IDs based on selected protection types
 * This ensures only relevant laws are included in the search
 *
 * @param {Array} lawIds - Array of law IDs
 * @param {Object} protections - Object with sexualOrientation and genderIdentity boolean flags
 * @returns {Array} Filtered array of law IDs
 */
const filterLawsByProtectionType = (lawIds, protections) => {
  if (!lawIds || lawIds.length === 0) return [];

  // If both protections are selected, return all laws
  if (protections.sexualOrientation && protections.genderIdentity) {
    return lawIds;
  }

  // Filter laws based on selected protection type
  return lawIds.filter(lawId => {
    const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];

    if (protections.sexualOrientation && !protections.genderIdentity) {
      // Only sexual orientation selected - include 'sexual' and 'both' laws
      return classifications.includes('sexual') || classifications.includes('both');
    }

    if (!protections.sexualOrientation && protections.genderIdentity) {
      // Only gender identity selected - include 'gender' and 'both' laws
      return classifications.includes('gender') || classifications.includes('both');
    }

    // No protections selected - include all laws (shouldn't happen in practice)
    return true;
  });
};

// Helper function to get law classification display text
const getLawClassificationDisplay = (lawId) => {
  const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
  if (classifications.includes('both')) return 'Both SO & GI';
  if (classifications.includes('sexual')) return 'Sexual Orientation';
  if (classifications.includes('gender')) return 'Gender Identity';
  return 'Unknown';
};

// Helper function to get classification color
const getLawClassificationColor = (lawId) => {
  const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
  if (classifications.includes('both')) return '#8B5CF6'; // Purple
  if (classifications.includes('sexual')) return '#F59E0B'; // Orange
  if (classifications.includes('gender')) return '#3B82F6'; // Blue
  return '#6B7280'; // Gray
};

// Helper function to get classification summary for a search option
const getSearchOptionClassificationSummary = (searchOption) => {
  const lawIds = searchOptionToLaws[searchOption] || [];
  if (lawIds.length === 0) return { text: 'No laws mapped', color: '#6B7280' };

  const classifications = new Set();
  lawIds.forEach(lawId => {
    const lawClassifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
    lawClassifications.forEach(c => classifications.add(c));
  });

  if (classifications.has('both') && classifications.size === 1) {
    return { text: 'Both SO & GI', color: '#8B5CF6' };
  } else if (classifications.has('sexual') && classifications.size === 1) {
    return { text: 'Sexual Orientation only', color: '#F59E0B' };
  } else if (classifications.has('gender') && classifications.size === 1) {
    return { text: 'Gender Identity only', color: '#3B82F6' };
  } else {
    // Mixed classifications
    const types = [];
    if (classifications.has('sexual')) types.push('SO');
    if (classifications.has('gender')) types.push('GI');
    if (classifications.has('both')) types.push('Both');
    return { text: `Mixed: ${types.join(', ')}`, color: '#6B7280' };
  }
};

const SearchOptions = () => {
  // State for tracking which search option is active (basic, advanced, or comparison)
  const [activeOption, setActiveOption] = useState(null);

  // State for controlling form animation
  const [animateForm, setAnimateForm] = useState(false);

  // State for form validation
  const [validationError, setValidationError] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  // State for testing controls visibility
  const [showTestingControls, setShowTestingControls] = useState(false);

  /**
   * Form data for all search options
   *
   * This state stores the user's preferences for each protection:
   * - null: No selection made yet
   * - "doesnt-matter": This protection won't affect search results
   * - "good-to-have": This protection is preferred but not required
   * - "must-have": This protection is required (locations without it will be eliminated)
   */
  const [formData, setFormData] = useState({
    // Basic search form fields
    employmentProtection: null,
    stateEmployees: null,
    relationship: null,
    healthcare: null,
    genderMarkers: null,
    youthProtections: null,
    criminalJustice: null,
    noBans: null,
    noBathroom: null,
    noExclusionarySexDefinition: null,
    noReligiousExemptions: null,

    // Advanced search form fields - initialized as null (no selection)
    employmentNonDiscrimination: null,
    housingNonDiscrimination: null,
    publicAccommodationNonDiscrimination: null,
    stateEmployeeNonDiscrimination: null,
    allowLocalLaws: null,
    noReligiousExemptionMarriage: null,
    secondParentAdoption: null,
    adoptionNonDiscrimination: null,
    fosterCareNonDiscrimination: null,
    assistedReproductionRecognition: null,
    lgbtInclusiveSpouseForLeave: null,
    lgbtInclusiveChildrenForLeave: null,
    privateHealthInsuranceNonDiscrimination: null,
    shieldLawsTransgenderCare: null,
    healthInsuranceTransgenderCoverage: null,
    medicaidTransgenderCoverage: null,
    stateEmployeeTransgenderBenefits: null,
    fertilityPrivateInsurance: null,
    fertilityMedicaid: null,
    transgenderYouthCare: null,
    noReligiousExemptionMedical: null,
    driverLicenseGenderMarker: null,
    birthCertificateGenderMarker: null,
    bathroomAccess: null,
    studentNonDiscrimination: null,
    allowLocalStudentProtections: null,
    antibullyingLaws: null,
    allowLocalAntibullyingPolicies: null,
    conversionTherapyBan: null,
    transgenderSportsParticipation: null,
    childWelfareSystemProtections: null,
    lgbtInclusiveCurriculum: null,
    noParentalOptOut: null,
    noForcedOuting: null,
    noDontSayGay: null,
    noReligiousExemptionChildWelfare: null,
    hateCrimeLaws: null,
    noPanicDefense: null,
    noDragRestrictions: null
  });

  // Router hooks for navigation and accessing location state
  const navigate = useNavigate();
  const location = useLocation();

  // Extract data from location state (from previous screen)
  const zipCode = location.state?.zipCode || '';
  const protections = location.state?.protections || { sexualOrientation: false, genderIdentity: false };
  const populationCategories = location.state?.populationCategories || {};
  const ageStatus = location.state?.ageStatus || '';
  const tieBreaker = location.state?.tieBreaker || '';
  const sessionId = location.state?.sessionId || '';

  /**
   * Basic search options configuration
   */
  const basicOptions = [
    { name: 'employmentProtection', label: 'Non-discrimination in EMPLOYMENT, HOUSING & PUBLIC ACCOMMODATION' },
    { name: 'stateEmployees', label: 'Non-discrimination for STATE EMPLOYEES' },
    { name: 'relationship', label: 'Inclusive relationship and parental recognition' },
    { name: 'healthcare', label: 'Inclusive healthcare laws & policies' },
    { name: 'genderMarkers', label: "Inclusive gender markers (driver's licenses, birth certificates)" },
    { name: 'youthProtections', label: 'Inclusive youth protections' },
    { name: 'criminalJustice', label: 'Inclusive criminal justice' },
    { name: 'noBans', label: 'State does NOT ban cities and counties from passing non-discrimination laws' },
    { name: 'noBathroom', label: 'State does NOT ban cities and counties from passing "bathroom laws"' },
    { name: 'noExclusionarySexDefinition', label: 'State does NOT ban laws recognizing existence of transgender people' },
    { name: 'noReligiousExemptions', label: 'State does NOT allow for religious exemptions to equality' }
  ];

  /**
   * Advanced search sections configuration
   *
   * NOTE: Items commented out are NOT IN DESIGN SPECIFICATION but exist in the database
   * Laws not in design: 2, 3, 12, 28, 30, 40, 41, 44, 45, 48, 49, 53, 54, 55
   */
  const advancedSections = [
    {
      title: 'BROAD PROTECTIONS:',
      fields: [
        { name: 'employmentNonDiscrimination', label: 'Employment Non-discrimination' },
        { name: 'housingNonDiscrimination', label: 'Housing Non-discrimination' },
        { name: 'publicAccommodationNonDiscrimination', label: 'Public Accommodation Non-discrimination' },
        { name: 'stateEmployeeNonDiscrimination', label: 'Non-discrimination for STATE EMPLOYEES' },
        { name: 'allowLocalLaws', label: 'State allows cities and counties to pass non-discrimination laws, even if there is no state-wide law' },
        { name: 'noReligiousExemptionMarriage', label: 'State does NOT allow targeted religious exemption for state and local officials providing marriage licenses' }
      ]
    },
    {
      title: 'RELATIONSHIP & PARENTAL RECOGNITION:',
      fields: [
        { name: 'secondParentAdoption', label: 'Second-parent adoption for unmarried couples' },
        { name: 'adoptionNonDiscrimination', label: 'Adoption non-discrimination protections for LGBT parents' },
        { name: 'fosterCareNonDiscrimination', label: 'Foster-care non-discrimination protections for LGBT parents' },
        { name: 'assistedReproductionRecognition', label: 'Recognition for parents using assisted reproduction' },
        { name: 'lgbtInclusiveSpouseForLeave', label: 'LGBT-inclusive definitions of spouse/partner for family medical leave' },
        { name: 'lgbtInclusiveChildrenForLeave', label: 'LGBT-inclusive definitions of children/parents for family medical leave' }
      ]
    },
    {
      title: 'INCLUSIVE HEALTHCARE LAWS & POLICIES:',
      fields: [
        { name: 'privateHealthInsuranceNonDiscrimination', label: 'Private health insurance non-discrimination laws' },
        { name: 'shieldLawsTransgenderCare', label: 'Shield laws protecting access to transgender health care' },
        { name: 'healthInsuranceTransgenderCoverage', label: 'Health insurance providers banned from excluding coverage for transgender-specific care' },
        { name: 'medicaidTransgenderCoverage', label: 'State Medicaid policy coverage for transgender-specific care' },
        { name: 'stateEmployeeTransgenderBenefits', label: 'Transgender-inclusive health benefits for state employees' },
        { name: 'fertilityPrivateInsurance', label: 'Fertility treatment coverage in private insurance' },
        { name: 'fertilityMedicaid', label: 'Fertility treatment coverage in Medicaid' },
        { name: 'transgenderYouthCare', label: 'State allows transgender-specific care for youth' },
        { name: 'noReligiousExemptionMedical', label: 'State does NOT allow targeted religious exemption for medical professionals' }
      ]
    },
    {
      title: 'GENDER MARKERS & TRANS POLICIES:',
      fields: [
        { name: 'driverLicenseGenderMarker', label: "Ability to change gender marker on Driver's License" },
        { name: 'birthCertificateGenderMarker', label: 'Ability to change gender marker on Birth Certificate' },
        { name: 'bathroomAccess', label: "State allows transgender people to use bathrooms/facilities consistent with their gender identity in gov't owned buildings" },
        { name: 'noExclusionarySexDefinition', label: 'State does NOT explicitly define "sex" throughout state law to exclude transgender people' }
      ]
    },
    {
      title: 'LGBT YOUTH:',
      fields: [
        { name: 'studentNonDiscrimination', label: 'Non-discrimination laws and policies covering LGBT students' },
        { name: 'allowLocalStudentProtections', label: 'State allows local schools and districts to pass non-discrimination laws covering LGBT students, even if there is no state-wide law' },
        { name: 'antibullyingLaws', label: 'Anti-bullying laws and policies covering LGBT students' },
        { name: 'allowLocalAntibullyingPolicies', label: 'State allows local schools and districts to have anti-bullying laws and policies covering LGBT students, even if there is no state-wide law' },
        { name: 'conversionTherapyBan', label: 'Conversion therapy ban covering LGBT youth' },
        { name: 'transgenderSportsParticipation', label: 'Allows transgender students to participate in sports consistent with their gender identity' },
        { name: 'childWelfareSystemProtections', label: 'Allows protections for LGBT youth in Child Welfare System' },
        { name: 'lgbtInclusiveCurriculum', label: 'State curricular standards required to be LGBT-inclusive' },
        { name: 'noParentalOptOut', label: 'State does NOT require parental notification and opt-out rights of LGBT-inclusive curricula' },
        { name: 'noForcedOuting', label: 'State does NOT force the outing of transgender youth in schools' },
        { name: 'noDontSayGay', label: 'State does NOT have a "Don\'t Say Gay" law barring or restricting discussion of LGBT people in schools' },
        { name: 'noReligiousExemptionChildWelfare', label: 'State does NOT allow targeted religious exemption for child welfare services' }
      ]
    },
    {
      title: 'CRIMINAL JUSTICE:',
      fields: [
        { name: 'hateCrimeLaws', label: 'Hate crime laws covering LGBT people' },
        { name: 'noPanicDefense', label: 'Ban on "panic defense"' },
        { name: 'noDragRestrictions', label: 'State does NOT restrict drag performances' }
      ]
    }
  ];

  useEffect(() => {
    // Smooth scroll to top when component mounts
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  /**
   * Initialize with location state data from previous screen if available
   */
  useEffect(() => {
    if (location.state) {
      const { zipCode, populationCategories, ageStatus, protections, sessionId, tieBreaker } = location.state;
      console.log('SearchOptions - Data from previous screen:', {
        zipCode,
        populationCategories,
        ageStatus,
        protections,
        sessionId,
        tieBreaker
      });
    }
  }, [location.state]);

  /**
   * Handle returning from ResultsScreen with form data
   */
  useEffect(() => {
    // Check if we're returning from a failed search
    if (location.state?.returnedFormData) {
      console.log('SearchOptions: Restoring form data from previous search');
      console.log('Returned form data:', location.state.returnedFormData);
      
      // Set the form data
      setFormData(location.state.returnedFormData);
      
      // Restore the search type and show the form
      if (location.state.returnedSearchType) {
        setActiveOption(location.state.returnedSearchType);
        setTimeout(() => {
          setAnimateForm(true);
        }, 100);
      }
    }
  }, [location.state?.returnedFormData]);

  /**
   * Sticky header effect
   * This creates a beautiful floating effect when the table headers stick to the top
   */
  useEffect(() => {
    if (!activeOption) return;

    const handleScroll = () => {
      const headers = document.querySelectorAll('.table-header');

      headers.forEach(header => {
        const rect = header.getBoundingClientRect();
        const scrolled = window.scrollY > 100;

        if (scrolled && rect.top <= 10) {
          header.classList.add('is-stuck');
        } else {
          header.classList.remove('is-stuck');
        }
      });
    };

    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollListener);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, [activeOption]);

  /**
   * Handle selecting a search option (Basic, Advanced, or Comparison)
   */
  const handleOptionSelect = (option) => {
    const selectionDiv = document.querySelector('.search-type-selection');
    if (selectionDiv) {
      selectionDiv.classList.add('fade-out');
    }
  
    setTimeout(() => {
      if (option === 'comparison') {
        navigate('/compare-search', {
          state: {
            zipCode,
            populationCategories,
            ageStatus,
            protections,
            sessionId,
            tieBreaker
          }
        });
        return;
      }
  
      setActiveOption(option);
  
      // ✅ Smooth scroll to top after activating form
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
  
      setTimeout(() => {
        setAnimateForm(true);
      }, 400);
    }, 300);
  };
  

  /**
   * Handle checkbox changes
   */
  const handleCheckboxChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName] === value ? null : value
    }));

    if (validationError) {
      setValidationError(false);
    }

    if (missingFields.includes(fieldName)) {
      setMissingFields(prev => prev.filter(field => field !== fieldName));
    }
  };

  /**
   * Validate form completion - Updated to only validate visible fields
   */
  const validateForm = () => {
    let requiredFields = [];

    if (activeOption === 'basic') {
      requiredFields = basicOptions
        .filter(option => shouldShowSearchOption(option.name, protections))
        .map(option => option.name);
    } else if (activeOption === 'advanced') {
      requiredFields = advancedSections.flatMap(section =>
        section.fields
          .filter(field => shouldShowSearchOption(field.name, protections))
          .map(field => field.name)
      );
    }

    const incomplete = requiredFields.filter(field => formData[field] === null);
    setMissingFields(incomplete);

    return incomplete.length === 0;
  };

  /**
   * Handle closing validation error
   */
  const handleCloseError = () => {
    setValidationError(false);
  };

  const handleBack = () => {
    setAnimateForm(false);
    setTimeout(() => {
      setActiveOption(null);
    }, 300);
  };

  /**
   * Testing helper functions - ONLY VISIBLE IN DEVELOPMENT MODE
   */
  const fillAllFields = (value) => {
    const newFormData = { ...formData };

    if (activeOption === 'basic') {
      basicOptions
        .filter(option => shouldShowSearchOption(option.name, protections))
        .forEach(option => {
          newFormData[option.name] = value;
        });
    } else if (activeOption === 'advanced') {
      advancedSections.forEach(section => {
        section.fields
          .filter(field => shouldShowSearchOption(field.name, protections))
          .forEach(field => {
            newFormData[field.name] = value;
          });
      });
    }

    setFormData(newFormData);
    setValidationError(false);
    setMissingFields([]);
  };

  const fillRandomFields = () => {
    const values = ['doesnt-matter', 'good-to-have', 'must-have'];
    const newFormData = { ...formData };

    if (activeOption === 'basic') {
      basicOptions
        .filter(option => shouldShowSearchOption(option.name, protections))
        .forEach(option => {
          newFormData[option.name] = values[Math.floor(Math.random() * values.length)];
        });
    } else if (activeOption === 'advanced') {
      advancedSections.forEach(section => {
        section.fields
          .filter(field => shouldShowSearchOption(field.name, protections))
          .forEach(field => {
            newFormData[field.name] = values[Math.floor(Math.random() * values.length)];
          });
      });
    }

    setFormData(newFormData);
    setValidationError(false);
    setMissingFields([]);
  };

  const clearAllFields = () => {
    const newFormData = { ...formData };

    if (activeOption === 'basic') {
      basicOptions.forEach(option => {
        newFormData[option.name] = null;
      });
    } else if (activeOption === 'advanced') {
      advancedSections.forEach(section => {
        section.fields.forEach(field => {
          newFormData[field.name] = null;
        });
      });
    }

    setFormData(newFormData);
  };

  const testSpecificLaws = (testCase) => {
    const newFormData = { ...formData };

    // Clear all fields first
    Object.keys(newFormData).forEach(key => {
      newFormData[key] = 'doesnt-matter';
    });

    // Apply specific test cases
    switch (testCase) {
      case 'negative-laws':
        newFormData.allowLocalLaws = 'must-have';
        newFormData.bathroomAccess = 'must-have';
        newFormData.transgenderYouthCare = 'must-have';
        newFormData.noDragRestrictions = 'must-have';
        break;
      case 'compound-basic':
        newFormData.employmentProtection = 'must-have';
        newFormData.healthcare = 'must-have';
        newFormData.noReligiousExemptions = 'must-have';
        break;
      case 'youth-focus':
        newFormData.youthProtections = 'must-have';
        newFormData.conversionTherapyBan = 'must-have';
        newFormData.studentNonDiscrimination = 'must-have';
        newFormData.antibullyingLaws = 'must-have';
        break;
      case 'trans-rights':
        newFormData.genderMarkers = 'must-have';
        newFormData.bathroomAccess = 'must-have';
        newFormData.transgenderYouthCare = 'must-have';
        newFormData.transgenderSportsParticipation = 'must-have';
        break;
      default:
        break;
    }

    setFormData(newFormData);
    setValidationError(false);
    setMissingFields([]);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e) => {
    e.preventDefault();
  
    // Validate form completion
    if (!validateForm()) {
      setValidationError(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
  
    // Clear any previous validation errors
    setValidationError(false);
    setMissingFields([]);
  
    // Create filtered form data with only applicable laws
    const filteredFormData = {};
    Object.keys(formData).forEach(fieldName => {
      const fieldValue = formData[fieldName];
      if (fieldValue !== null) {
        const lawIds = searchOptionToLaws[fieldName] || [];
        const filteredLaws = filterLawsByProtectionType(lawIds, protections);
        if (filteredLaws.length > 0) {
          filteredFormData[fieldName] = fieldValue;
        }
      }
    });
  
    // ✅ Smooth scroll to top BEFORE navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
  
    // Small delay lets the scroll start, feels smoother
    setTimeout(() => {
      navigate('/results', {
        state: {
          zipCode,
          formData: filteredFormData,
          originalFormData: formData,
          searchType: activeOption,
          protections,
          populationCategories,
          ageStatus,
          tieBreaker,
          sessionId
        }
      });
    }, 120);
  };
  

  /**
   * Render checkbox options for a form field
   */
  const renderCheckboxOptions = (name, value) => (
    <div className="checkbox-row">
      <div className="checkbox-option">
        <input
          type="checkbox"
          id={`${name}-doesnt-matter`}
          checked={value === 'doesnt-matter'}
          onChange={() => handleCheckboxChange(name, 'doesnt-matter')}
        />
        <label htmlFor={`${name}-doesnt-matter`}></label>
      </div>
      <div className="checkbox-option">
        <input
          type="checkbox"
          id={`${name}-good-to-have`}
          checked={value === 'good-to-have'}
          onChange={() => handleCheckboxChange(name, 'good-to-have')}
        />
        <label htmlFor={`${name}-good-to-have`}></label>
      </div>
      <div className="checkbox-option">
        <input
          type="checkbox"
          id={`${name}-must-have`}
          checked={value === 'must-have'}
          onChange={() => handleCheckboxChange(name, 'must-have')}
        />
        <label htmlFor={`${name}-must-have`}></label>
      </div>
    </div>
  );

  /**
   * Helper function to get selected population categories as a readable string
   */
  const getSelectedPopulationCategories = () => {
    const selected = [];

    if (populationCategories.under10k) selected.push('Under 10k');
    if (populationCategories.from10kTo50k) selected.push('10k-50k');
    if (populationCategories.from50kTo200k) selected.push('50k-200k');
    if (populationCategories.from200kTo1m) selected.push('200k-1M');
    if (populationCategories.over1m) selected.push('Over 1M');

    if (selected.length === 0) return 'All sizes';
    if (selected.length === 5) return 'All sizes';
    if (selected.length === 1) return selected[0];
    if (selected.length === 2) return selected.join(' and ');

    // For 3 or more selections, use commas with "and" before the last item
    const lastItem = selected.pop();
    return selected.join(', ') + ', and ' + lastItem;
  };

  /**
   * Helper function to get tie breaker display text
   */
  const getTieBreakerDisplay = () => {
    switch(tieBreaker) {
      case 'resources':
        return 'Number of Resources';
      case 'rent':
        return 'Rental Costs';
      case 'distance':
        return 'Distance from Current Location';
      default:
        return 'Not specified';
    }
  };

  useEffect(() => {
    const adjustSquareHeights = () => {
      const container = document.querySelector('.search-options-container');
      const square1 = document.querySelector('.search-options-purple-square');
      const square2 = document.querySelector('.search-options-purple-square-2');

      if (container && square1 && square2) {
        // Get base height based on screen width (matching CSS breakpoints)
        const screenWidth = window.innerWidth;
        let baseHeight;
        
        if (screenWidth >= 2560) {
          baseHeight = 1100;
        } else if (screenWidth >= 1920) {
          baseHeight = 950;
        } else if (screenWidth >= 1600) {
          baseHeight = 700;
        } else if (screenWidth >= 1280) {
          baseHeight = 620;
        } else if (screenWidth >= 1024) {
          baseHeight = 550;
        } else {
          baseHeight = 700; // Hidden on mobile anyway
        }
        
        // Reset to base height when no option is selected
        if (!activeOption) {
          square1.style.height = `${baseHeight}px`;
          square2.style.height = `${baseHeight}px`;
        } else {
          // Use different multipliers for basic vs advanced
          const containerHeight = container.scrollHeight;
          const multiplier = activeOption === 'advanced' ? 0.993 : 0.98;

          square1.style.height = `${Math.max(baseHeight, containerHeight * multiplier)}px`;
          square2.style.height = `${Math.max(baseHeight, containerHeight * multiplier)}px`;
        }
      }
    };

    // Initial adjustment
    adjustSquareHeights();

    // Adjust with delay when form changes
    if (activeOption || activeOption === null) {
      setTimeout(adjustSquareHeights, 100);
    }

    // Also adjust when animation state changes
    if (animateForm === false) {
      setTimeout(adjustSquareHeights, 400);
    }

    // Adjust on window resize
    window.addEventListener('resize', adjustSquareHeights);

    return () => {
      window.removeEventListener('resize', adjustSquareHeights);
    };
  }, [activeOption, animateForm]);

  /**
   * Main component render
   */
  return (
    <div className="search-options-container">
      <Helmet>
        <title>Search Options | LGBT PridePath</title>
        <meta name="description" content="Customize your search for LGBTQ+ friendly communities. Select protection categories including employment, housing, healthcare, and youth protections." />
        <link rel="canonical" href="https://www.lgbtpridepath.org/search-options" />
      </Helmet>
      
      {/* Light purple curved area */}
      <div className="light-purple-curve"></div>
      <div className="search-options-purple-square"></div>
      <div className="search-options-purple-square-2"></div>

      {/* SVG curve option (smoother alternative) */}
      <div className="svg-curve">
        <svg viewBox="0 0 1200 250" preserveAspectRatio="none">
          <path
            d="M0,0 Q600,200 1200,0 L1200,250 L0,250 Z"
            fill="#8B5CF6"
            fillOpacity="0.2"
          />
        </svg>
      </div>

      <div className="search-options-card">
        {/* Header section with title and subtitle */}
        <div className="search-options-header">
          <div className="header-content">
            <h1>Find LGBTQ+ Protections</h1>
            <p className="header-subtitle">
              Select which protections matter to you
            </p>
            {/* Display selected protection types and population categories */}
            {(protections.sexualOrientation || protections.genderIdentity) && (
              <div className="protection-info">
                <p className="protection-types">
                  Searching for: {protections.sexualOrientation && protections.genderIdentity ? 'Both Sexual Orientation & Gender Identity' :
                    protections.sexualOrientation ? 'Sexual Orientation' : 'Gender Identity'} protections
                </p>
                <p className="population-selection">
                  City sizes: <span className="selected-populations">{getSelectedPopulationCategories()}</span>
                </p>
                {tieBreaker && (
                  <p className="tiebreaker-selection">
                    Tie-breaking preference: <span className="selected-tiebreaker">{getTieBreakerDisplay()}</span>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="search-options-body">
          {/* Selection buttons - shown when no option is active */}
          {!activeOption && (
            <div className="search-type-selection">
              <h2>STEP 2:</h2>
              <div className="option-buttons">
                <button
                  className="option-button basic"
                  onClick={() => handleOptionSelect('basic')}
                >
                  <div className="button-content">
                    <span className="icon">🔍</span>
                    <span className="text">BASIC SEARCH</span>
                  </div>
                </button>
                <button
                  className="option-button advanced"
                  onClick={() => handleOptionSelect('advanced')}
                >
                  <div className="button-content">
                    <span className="icon">⚙️</span>
                    <span className="text">ADVANCED SEARCH</span>
                  </div>
                </button>
                <button
                  className="option-button comparison"
                  onClick={() => handleOptionSelect('comparison')}
                >
                  <div className="button-content">
                    <span className="icon">⚖️</span>
                    <span className="text">CITY COMPARISON</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Basic Search Form - shown when 'basic' option is active */}
          {activeOption === 'basic' && (
            <div className={`search-form-container ${animateForm ? 'animate-in' : 'animate-out'}`}>
              {/* Form header with back button and title */}
              <div className="form-header">
                <button
                  className="back-button"
                  onClick={handleBack}
                  aria-label="Go back to search options"
                >
                  ←
                </button>
                <h2>BASIC SEARCH</h2>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Form instructions */}
                <div className="form-instructions">
                  <h3>STEP 3:</h3>
                  <p className="instructions">
                    Which laws are you interested in? (check all that apply)<br />
                    For EACH line, choose ONE of the following:
                  </p>
                  <div className="legend">
                    <div className="legend-item">
                      <span>DOESN'T MATTER (results will not include this)</span>
                    </div>
                    <div className="legend-item">
                      <span>GOOD TO HAVE (law that is not absolutely necessary)</span>
                    </div>
                    <div className="legend-item">
                      <span>MUST HAVE <span className="deal-breaker">DEAL-BREAKER</span> (automatically eliminate any location without this)</span>
                    </div>
                  </div>
                </div>

                {/* Validation error message */}
                {validationError && (
                  <div className="validation-error">
                    <div className="error-icon">⚠️</div>
                    <div className="error-text">
                      <strong>Please complete all selections</strong>
                      <p>You must select one option for each protection before searching.</p>
                    </div>
                    <button
                      className="error-close"
                      onClick={handleCloseError}
                      aria-label="Close error message"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Protection Type Legend 
                <div style={{
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  marginBottom: '0.75rem',
                  fontSize: '0.75rem',
                  lineHeight: '1.3'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#8B5CF6',
                        borderRadius: '2px',
                        flexShrink: 0
                      }}></span>
                      <span><strong>Both SO & GI</strong> - Sexual Orientation & Gender Identity</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#F59E0B',
                        borderRadius: '2px',
                        flexShrink: 0
                      }}></span>
                      <span><strong>SO only</strong> - Sexual Orientation protections</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#3B82F6',
                        borderRadius: '2px',
                        flexShrink: 0
                      }}></span>
                      <span><strong>GI only</strong> - Gender Identity protections</span>
                    </div>
                  </div>
                </div> 
                */}

                {/* Testing Controls - Always visible (comment out or delete before production)
                <div className="testing-controls" style={{
                  background: '#333',
                  border: '2px solid #666',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  overflow: 'hidden'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowTestingControls(!showTestingControls)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#444',
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>🧪 Testing Tools</span>
                    <span style={{ fontSize: '12px' }}>{showTestingControls ? '▼' : '▶'}</span>
                  </button>
                  {showTestingControls && (
                    <div style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => fillAllFields('doesnt-matter')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          All "Doesn't Matter"
                        </button>
                        <button
                          type="button"
                          onClick={() => fillAllFields('good-to-have')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          All "Good to Have"
                        </button>
                        <button
                          type="button"
                          onClick={() => fillAllFields('must-have')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          All "Must Have"
                        </button>
                        <button
                          type="button"
                          onClick={fillRandomFields}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          Random Fill
                        </button>
                        <button
                          type="button"
                          onClick={clearAllFields}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          Clear All
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('negative-laws')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#4a5568', color: '#fff', border: '1px solid #2d3748', borderRadius: '4px' }}
                        >
                          Test Negative Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('compound-basic')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#38a169', color: '#fff', border: '1px solid #2f855a', borderRadius: '4px' }}
                        >
                          Test Compound Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('youth-focus')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#d69e2e', color: '#fff', border: '1px solid #b7791f', borderRadius: '4px' }}
                        >
                          Test Youth Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('trans-rights')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#e53e3e', color: '#fff', border: '1px solid #c53030', borderRadius: '4px' }}
                        >
                          Test Trans Rights
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('negative-laws')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#4a5568', color: '#fff', border: '1px solid #2d3748', borderRadius: '4px' }}
                        >
                          Test Negative Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('compound-basic')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#38a169', color: '#fff', border: '1px solid #2f855a', borderRadius: '4px' }}
                        >
                          Test Compound Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('youth-focus')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#d69e2e', color: '#fff', border: '1px solid #b7791f', borderRadius: '4px' }}
                        >
                          Test Youth Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('trans-rights')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#e53e3e', color: '#fff', border: '1px solid #c53030', borderRadius: '4px' }}
                        >
                          Test Trans Rights
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                 */}

                {/* Column headers */}
                <div className="table-header">
                  <div className="header-column">DOESN'T<br />MATTER</div>
                  <div className="header-column">GOOD<br />TO<br />HAVE</div>
                  <div className="header-column">MUST<br />HAVE<br /><span className="deal-breaker">DEAL-<br />BREAKER</span></div>
                  <div className="header-column protections-header">PROTECTIONS</div>
                </div>

                {/* Basic search form options - FILTERED */}
                <div className="form-options">
                  <div className="options-table">
                    {basicOptions
                      .filter(option => shouldShowSearchOption(option.name, protections))
                      .map(option => {
                        const classification = getSearchOptionClassificationSummary(option.name);
                        return (
                          <div key={option.name} className={`table-row ${missingFields.includes(option.name) ? 'incomplete' : ''}`}>
                            <div className="option-checkboxes">
                              {renderCheckboxOptions(option.name, formData[option.name])}
                            </div>
                            <div className="option-label">
                              {option.label}
                              <Tooltip
                                content={
                                  <div>
                                    <div style={{ marginBottom: '0.5rem' }}>
                                      <strong>Applies to: {classification.text}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                      Laws included: {searchOptionToLaws[option.name]?.join(', ') || 'None'}
                                    </div>
                                  </div>
                                }
                              >
                                {/* Classification tag - COMMENTED OUT

                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: classification.color,
                                  color: 'white',
                                  borderRadius: '12px',
                                  padding: '0.15rem 0.5rem',
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold',
                                  marginLeft: '0.5rem',
                                  verticalAlign: 'middle',
                                  cursor: 'help',
                                  title: classification.text === 'Both SO & GI' ? 'This protection applies to both Sexual Orientation and Gender Identity' :
                                    classification.text === 'Sexual Orientation only' ? 'This protection applies only to Sexual Orientation (e.g., gay, lesbian, bisexual)' :
                                      classification.text === 'Gender Identity only' ? 'This protection applies only to Gender Identity (e.g., transgender, non-binary)' :
                                        'Protection classification'
                                }}>
                                  {classification.text}
                                </span>
                                */}
                              </Tooltip>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Form submit button */}
                <div className="form-submit">
                  <button type="submit" className="submit-button">
                    SEARCH
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Advanced Search Form - shown when 'advanced' option is active */}
          {activeOption === 'advanced' && (
            <div className={`search-form-container ${animateForm ? 'animate-in' : 'animate-out'}`}>
              {/* Form header with back button and title */}
              <div className="form-header">
                <button
                  className="back-button"
                  onClick={handleBack}
                  aria-label="Go back to search options"
                >
                  ←
                </button>
                <h2>ADVANCED SEARCH</h2>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Form instructions */}
                <div className="form-instructions">
                  <h3>STEP 3:</h3>
                  <p className="instructions">
                    Which laws are you interested in? (check all that apply)<br />
                    For EACH line, choose ONE of the following:
                  </p>
                  <div className="legend">
                    <div className="legend-item">
                      <span>DOESN'T MATTER (results will not include this)</span>
                    </div>
                    <div className="legend-item">
                      <span>GOOD TO HAVE (law that is not absolutely necessary)</span>
                    </div>
                    <div className="legend-item">
                      <span>MUST HAVE <span className="deal-breaker">DEAL-BREAKER</span> (automatically eliminate any location without this)</span>
                    </div>
                  </div>
                </div>

                {/* Validation error message */}
                {validationError && (
                  <div className="validation-error">
                    <div className="error-icon">⚠️</div>
                    <div className="error-text">
                      <strong>Please complete all selections</strong>
                      <p>You must select one option for each protection before searching.</p>
                    </div>
                    <button
                      className="error-close"
                      onClick={handleCloseError}
                      aria-label="Close error message"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Protection Type Legend - NOW MATCHES BASIC SEARCH STYLE 
                <div style={{
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  marginBottom: '0.75rem',
                  fontSize: '0.75rem',
                  lineHeight: '1.3'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#8B5CF6',
                        borderRadius: '2px',
                        flexShrink: 0
                      }}></span>
                      <span><strong>Both SO & GI</strong> - Sexual Orientation & Gender Identity</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#F59E0B',
                        borderRadius: '2px',
                        flexShrink: 0
                      }}></span>
                      <span><strong>SO only</strong> - Sexual Orientation protections</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#3B82F6',
                        borderRadius: '2px',
                        flexShrink: 0
                      }}></span>
                      <span><strong>GI only</strong> - Gender Identity protections</span>
                    </div>
                  </div>
                </div>
                */}

                {/* Testing Controls - Always visible (comment out or delete before production)
                <div className="testing-controls" style={{
                  background: '#333',
                  border: '2px solid #666',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  overflow: 'hidden'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowTestingControls(!showTestingControls)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#444',
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>🧪 Testing Tools</span>
                    <span style={{ fontSize: '12px' }}>{showTestingControls ? '▼' : '▶'}</span>
                  </button>
                  {showTestingControls && (
                    <div style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => fillAllFields('doesnt-matter')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          All "Doesn't Matter"
                        </button>
                        <button
                          type="button"
                          onClick={() => fillAllFields('good-to-have')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          All "Good to Have"
                        </button>
                        <button
                          type="button"
                          onClick={() => fillAllFields('must-have')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          All "Must Have"
                        </button>
                        <button
                          type="button"
                          onClick={fillRandomFields}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          Random Fill
                        </button>
                        <button
                          type="button"
                          onClick={clearAllFields}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#666', color: '#fff', border: '1px solid #888', borderRadius: '4px' }}
                        >
                          Clear All
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('negative-laws')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#4a5568', color: '#fff', border: '1px solid #2d3748', borderRadius: '4px' }}
                        >
                          Test Negative Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('compound-basic')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#38a169', color: '#fff', border: '1px solid #2f855a', borderRadius: '4px' }}
                        >
                          Test Compound Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('youth-focus')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#d69e2e', color: '#fff', border: '1px solid #b7791f', borderRadius: '4px' }}
                        >
                          Test Youth Laws
                        </button>
                        <button
                          type="button"
                          onClick={() => testSpecificLaws('trans-rights')}
                          style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: '#e53e3e', color: '#fff', border: '1px solid #c53030', borderRadius: '4px' }}
                        >
                          Test Trans Rights
                        </button>
                      </div>
                    </div>
                  )}
                </div> 
                */}

                {/* Column headers */}
                <div className="table-header">
                  <div className="header-column">DOESN'T<br />MATTER</div>
                  <div className="header-column">GOOD<br />TO<br />HAVE</div>
                  <div className="header-column">MUST<br />HAVE<br /><span className="deal-breaker">DEAL-<br />BREAKER</span></div>
                  <div className="header-column protections-header">PROTECTIONS</div>
                </div>

                {/* Advanced search form options - FILTERED by section */}
                <div className="form-options advanced-options">
                  {advancedSections.map((section, sectionIndex) => {
                    const filteredFields = section.fields.filter(field =>
                      shouldShowSearchOption(field.name, protections)
                    );

                    // Skip the entire section if no fields match
                    if (filteredFields.length === 0) return null;

                    return (
                      <div key={sectionIndex} className="section-group">
                        <h3 className="options-section-title">{section.title}</h3>
                        <div className="options-table">
                          {filteredFields.map(field => {
                            const classification = getSearchOptionClassificationSummary(field.name);
                            return (
                              <div key={field.name} className={`table-row ${missingFields.includes(field.name) ? 'incomplete' : ''}`}>
                                <div className="option-checkboxes">
                                  {renderCheckboxOptions(field.name, formData[field.name])}
                                </div>
                                <div className="option-label">
                                  {field.label}
                                  <Tooltip
                                    content={
                                      <div>
                                        <div style={{ marginBottom: '0.5rem' }}>
                                          <strong>Applies to: {classification.text}</strong>
                                        </div>
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                          Laws included: {searchOptionToLaws[field.name]?.join(', ') || 'None'}
                                        </div>
                                      </div>
                                    }
                                  >
                                    {/* Classification tag - COMMENTED OUT
                                    <span style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: classification.color,
                                      color: 'white',
                                      borderRadius: '12px',
                                      padding: '0.15rem 0.5rem',
                                      fontSize: '0.65rem',
                                      fontWeight: 'bold',
                                      marginLeft: '0.5rem',
                                      verticalAlign: 'middle',
                                      cursor: 'help'
                                    }}>
                                      {classification.text}
                                    </span>
                                    */}
                                  </Tooltip>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Form submit button */}
                <div className="form-submit">
                  <button type="submit" className="submit-button">
                    SEARCH
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchOptions;